import React, { useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Globe, Check, Loader2 } from "lucide-react";
import { BRAND } from "../data/copperx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name || !form.email || !form.message) {
      setErr("Please fill in your name, email and a message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (e2) {
      setErr("Could not submit. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-white py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="section-rule" />
              <span className="text-[#B85C38] text-xs tracking-[0.3em] uppercase font-semibold">
                Get in Touch
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-medium tracking-tight text-[#0F172A] leading-[1.05] mb-8">
              Talk to our <span className="text-[#B85C38]">technical team.</span>
            </h2>
            <p className="text-[#475569] text-lg leading-relaxed mb-10">
              Whether you need a single coil or a project-grade supply contract,
              we're here to help you specify the right copper for your application.
            </p>

            <ul className="space-y-6">
              <ContactRow
                Icon={MapPin}
                label="Headquarters"
                value={BRAND.address}
                testid="contact-info-address"
              />
              <ContactRow
                Icon={Phone}
                label="Phone"
                value={BRAND.phone}
                href={`tel:${BRAND.phoneHref}`}
                testid="contact-info-phone"
              />
              <ContactRow
                Icon={Mail}
                label="Email"
                value={BRAND.email}
                href={`mailto:${BRAND.email}`}
                testid="contact-info-email"
              />
              <ContactRow
                Icon={Globe}
                label="Service Regions"
                value="Middle East · Africa · Asia"
                testid="contact-info-regions"
              />
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-[#18181B] text-white p-8 md:p-12">
              {sent ? (
                <div data-testid="contact-success-card">
                  <div className="w-14 h-14 bg-[#B85C38] text-white flex items-center justify-center mb-6">
                    <Check size={28} />
                  </div>
                  <h3 className="text-3xl font-medium mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Message sent.
                  </h3>
                  <p className="text-[#94A3B8] mb-6">
                    We've received your message and will be in touch shortly.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    data-testid="contact-send-another-button"
                    className="text-[#E19672] font-medium hover:text-white transition-colors"
                  >
                    Send another →
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
                  <h3 className="text-2xl font-medium mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Send us a message
                  </h3>
                  <p className="text-[#94A3B8] text-sm mb-6">
                    We typically reply within one working day.
                  </p>

                  <DarkField label="Your Name" name="name" value={form.name} onChange={onChange} testid="contact-input-name" />
                  <DarkField label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="contact-input-email" />
                  <DarkField label="Subject" name="subject" value={form.subject} onChange={onChange} testid="contact-input-subject" />
                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#E19672] mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={4}
                      data-testid="contact-textarea-message"
                      className="w-full bg-transparent border border-white/20 focus:border-[#B85C38] focus:ring-1 focus:ring-[#B85C38] outline-none px-4 py-3 text-sm text-white placeholder:text-white/40"
                    />
                  </div>

                  {err && (
                    <div data-testid="contact-error" className="text-sm text-[#fca5a5] bg-[#7f1d1d]/30 border border-[#7f1d1d] px-4 py-3">
                      {err}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="bg-[#B85C38] hover:bg-[#D97757] text-white px-7 py-4 font-medium tracking-wide flex items-center gap-3 transition-colors disabled:opacity-60"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="animate-spin" size={18} /> Sending…
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ Icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-start gap-4">
      <div className="bg-[#F8F9FA] border border-[#E2E8F0] p-3 mt-1">
        <Icon size={18} className="text-[#B85C38]" strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-xs tracking-[0.25em] uppercase text-[#94A3B8] font-semibold mb-1">
          {label}
        </div>
        <div className="text-[#0F172A] text-base font-medium" data-testid={testid}>
          {value}
        </div>
      </div>
    </div>
  );
  return href ? (
    <li>
      <a href={href} className="block hover:opacity-80 transition-opacity">
        {inner}
      </a>
    </li>
  ) : (
    <li>{inner}</li>
  );
}

function DarkField({ label, name, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-xs tracking-[0.2em] uppercase font-semibold text-[#E19672] mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-transparent border border-white/20 focus:border-[#B85C38] focus:ring-1 focus:ring-[#B85C38] outline-none px-4 py-3 text-sm text-white"
      />
    </div>
  );
}
